// Resolve a task's chef-hat references to in-app glossary entries.
//
// A ScheduleEntry carries `chefHatRefs: string[]` — ids of
// prerequisites.skills / notes / hotspots that name the task (aggregated
// by the projection). The cooking-screen chef-hat surfaces that per-task
// help, so we resolve against all three buckets. In the cookpit corpus the
// per-task references are carried by HOTSPOTS (skills/notes ship no
// taskRefs), so excluding hotspots removed every hat — hence they are
// included here.
//
// v1 is glossary-only: the entry carries `.text` from the cookpit file;
// there is no external URL (that is explicitly v2+, README §12.2).

import type { ScheduleEntry } from '@cookchow/shared';

export interface NoteEntry {
  id: string;
  text: string;
  source: 'skill' | 'note' | 'hotspot';
}

interface PrereqItemLike {
  id: string;
  text: string;
}
interface RecipeLike {
  cookpit?: {
    prerequisites?: {
      skills?: PrereqItemLike[];
      notes?: PrereqItemLike[];
      hotspots?: PrereqItemLike[];
    };
  };
}

/** Build a refId → entry lookup over the recipe's skills, notes and hotspots. */
export function buildNoteLookup(recipe: unknown): Map<string, NoteEntry> {
  const map = new Map<string, NoteEntry>();
  const prereqs = (recipe as RecipeLike | null)?.cookpit?.prerequisites;
  if (!prereqs) return map;
  for (const s of prereqs.skills ?? []) {
    if (s && typeof s.id === 'string') map.set(s.id, { id: s.id, text: s.text, source: 'skill' });
  }
  for (const n of prereqs.notes ?? []) {
    if (n && typeof n.id === 'string') map.set(n.id, { id: n.id, text: n.text, source: 'note' });
  }
  for (const h of prereqs.hotspots ?? []) {
    if (h && typeof h.id === 'string') map.set(h.id, { id: h.id, text: h.text, source: 'hotspot' });
  }
  return map;
}

/** The glossary entries a set of refs resolve to, de-duplicated, ref order. */
export function notesForRefs(refs: readonly string[], lookup: Map<string, NoteEntry>): NoteEntry[] {
  const out: NoteEntry[] = [];
  const seen = new Set<string>();
  for (const ref of refs) {
    const entry = lookup.get(ref);
    if (entry && !seen.has(entry.id)) {
      seen.add(entry.id);
      out.push(entry);
    }
  }
  return out;
}

/** The glossary entries a task references, de-duplicated and in ref order. */
export function notesForTask(task: ScheduleEntry, lookup: Map<string, NoteEntry>): NoteEntry[] {
  return notesForRefs(task.chefHatRefs, lookup);
}
